(function(){
  'use strict';
  // Theme gift lines use properties _fft_cart_gift_auto / _fft_cart_gift_modal (not _free_gift) so bundle apps
  // that tag BOGO lines with _free_gift are left alone. Legacy _free_gift is only honored for variants of the configured gift products.
  // Settings live on the free-shipping snippet data-* attrs; turn both gift toggles off to stop cart changes and strip theme promo lines.

  if (typeof theme === 'undefined') {
    window.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  function init(){
    let isManagingGift = false;
    let managingGiftPromotions = false;
    let lastCartFetch = { ts: 0, data: null };
    const CART_TTL = 80;

    const giftProductByHandle = Object.create(null);
    const giftProductFetching = new Set();
    let giftModalEl = null;
    let giftModalProductHandle = '';
    let giftModalOpen = false;
    const dismissedModal = { auto: false, modal: false };
    let disabledCleanupTimer = null;

    async function getCartState(force = false){
      const now = Date.now();
      if (!force && lastCartFetch.data && (now - lastCartFetch.ts) < CART_TTL) return lastCartFetch.data;
      try {
        const res = await fetch('/cart.js', {cache: 'no-store'});
        if (!res.ok) throw new Error('cart fetch failed');
        const json = await res.json();
        lastCartFetch = { ts: now, data: json };
        return json;
      } catch (e){
        return lastCartFetch.data || null;
      }
    }

    function getContainers(){
      return document.querySelectorAll('[data-free-shipping-limit]');
    }

    function parseThemeGiftVariantSet(attr){
      const set = new Set();
      String(attr || '').split(',').forEach(s => {
        const n = parseInt(s.trim(), 10);
        if (!Number.isNaN(n)) set.add(n);
      });
      return set;
    }

    function giftVariantIdsFromProduct(product){
      return (product?.variants || []).map(v => Number(v.id)).filter(Number.isFinite);
    }

    function themeGiftVariantId(item){
      const n = Number(item?.variant_id);
      return Number.isFinite(n) ? n : NaN;
    }

    function variantInThemeGiftAllowlist(themeVariantSet, variantId){
      if (!themeVariantSet || themeVariantSet.size === 0) return false;
      return themeVariantSet.has(variantId);
    }

    /** Union DOM variant ids + gift product JSON so allowlist is never empty when handles exist. */
    async function resolveThemeGiftVariantSet(container, cfg){
      const merged = new Set(parseThemeGiftVariantSet(container?.getAttribute('data-fft-theme-gift-variant-ids')));
      const addByHandle = async (handle) => {
        if (!handle) return;
        const p = await ensureGiftProduct(handle);
        giftVariantIdsFromProduct(p).forEach(id => merged.add(id));
      };
      await addByHandle(cfg?.autoHandle);
      await addByHandle(cfg?.modalHandle);
      return merged;
    }

    function cartChangeBodyForRemoval(item, lineNumber1Based){
      if (item && item.key) return JSON.stringify({ id: item.key, quantity: 0 });
      return JSON.stringify({ line: lineNumber1Based, quantity: 0 });
    }

    /** _fft_* only on allowlisted variants; legacy _free_gift* only $0 lines on allowlist. */
    function promoLineKind(item, themeVariantSet){
      const p = item?.properties || {};
      const vid = themeGiftVariantId(item);
      if (String(p._fft_cart_gift_modal) === 'true'){
        if (!variantInThemeGiftAllowlist(themeVariantSet, vid)) return null;
        return 'modal';
      }
      if (String(p._fft_cart_gift_auto) === 'true'){
        if (!variantInThemeGiftAllowlist(themeVariantSet, vid)) return null;
        return 'auto';
      }
      if (!variantInThemeGiftAllowlist(themeVariantSet, vid)) return null;
      const lineCents = Number(item?.final_line_price ?? item?.final_price ?? 0);
      if (lineCents > 0) return null;
      if (String(p._free_gift_modal) === 'true') return 'modal';
      if (String(p._free_gift_auto) === 'true') return 'auto';
      if (String(p._free_gift) === 'true') return 'legacy';
      return null;
    }

    function isPromoGiftLine(item, themeVariantSet){
      return promoLineKind(item, themeVariantSet) !== null;
    }

    async function removePromotionalGiftLines(){
      if (isManagingGift || managingGiftPromotions) return;
      const c = getContainers()[0];
      if (!c) return;
      const cfg = readUiConfig(c);
      const themeVariantSet = await resolveThemeGiftVariantSet(c, cfg);
      const cart = await getCartState(true);
      if (!cart?.items?.length) return;
      const targets = [];
      cart.items.forEach((item, idx) => {
        if (isPromoGiftLine(item, themeVariantSet)) targets.push({ item, line: idx + 1 });
      });
      if (!targets.length) return;
      targets.sort((a, b) => b.line - a.line);
      isManagingGift = true;
      try {
        for (const t of targets) {
          const res = await fetch(window.theme?.routes?.cart_change_url || '/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: cartChangeBodyForRemoval(t.item, t.line)
          });
          if (!res.ok) break;
        }
        lastCartFetch = { ts: 0, data: null };
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      } catch (e) {
        console.error(e);
      } finally {
        setTimeout(() => { isManagingGift = false; }, 50);
      }
    }

    async function ensureGiftProduct(handle){
      if (!handle) return null;
      if (giftProductByHandle[handle]) return giftProductByHandle[handle];
      if (giftProductFetching.has(handle)) return null;
      giftProductFetching.add(handle);
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { cache: 'no-store' });
        if (!res.ok) throw new Error('gift product fetch failed');
        const product = await res.json();
        giftProductByHandle[handle] = product;
        return product;
      } catch (e) {
        console.error(e);
        return null;
      } finally {
        giftProductFetching.delete(handle);
      }
    }

    function cartHasPromoFor(cart, giftVariantIds, kind, themeVariantSet){
      if (!cart?.items?.length || !giftVariantIds?.length) return false;
      const idSet = new Set(giftVariantIds);
      return cart.items.some(i => {
        if (!idSet.has(themeGiftVariantId(i))) return false;
        const pk = promoLineKind(i, themeVariantSet);
        if (pk === 'legacy') return true;
        if (pk === 'auto') return kind === 'auto';
        if (pk === 'modal') return kind === 'modal';
        return false;
      });
    }

    async function removePromoGiftVariantLines(giftVariantIds, kind, themeVariantSet){
      if (isManagingGift || !giftVariantIds?.length) return;
      const cart = await getCartState(true);
      if (!cart?.items?.length) return;
      const idSet = new Set(giftVariantIds);
      const targets = [];
      cart.items.forEach((item, idx) => {
        if (!idSet.has(themeGiftVariantId(item))) return;
        const pk = promoLineKind(item, themeVariantSet);
        const match = kind === 'auto'
          ? (pk === 'auto' || pk === 'legacy')
          : (pk === 'modal' || pk === 'legacy');
        if (match) targets.push({ item, line: idx + 1 });
      });
      if (!targets.length) return;
      targets.sort((a, b) => b.line - a.line);
      isManagingGift = true;
      try {
        for (const t of targets) {
          const res = await fetch(window.theme?.routes?.cart_change_url || '/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: cartChangeBodyForRemoval(t.item, t.line)
          });
          if (!res.ok) break;
        }
        lastCartFetch = { ts: 0, data: null };
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      } catch (e) {
        console.error(e);
      } finally {
        setTimeout(() => { isManagingGift = false; }, 50);
      }
    }

    function giftNeedsVariantPicker(product){
      return (product?.variants || []).length > 1;
    }

    function ensureGiftModalStyles(){
      if (document.getElementById('free-shirt-modal-styles')) return;
      const style = document.createElement('style');
      style.id = 'free-shirt-modal-styles';
      style.textContent = `
        .free-shirt-modal { position: fixed; inset: 0; z-index: 10000; display: none; }
        .free-shirt-modal.is-open { display: block; }
        .free-shirt-modal__backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.55); }
        .free-shirt-modal__content {
          position: relative;
          width: min(720px, 92vw);
          margin: 6vh auto;
          background: #fff;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.28);
          transform: translateY(8px);
          opacity: 0;
          transition: opacity .2s ease, transform .2s ease;
        }
        .free-shirt-modal.is-open .free-shirt-modal__content { opacity: 1; transform: translateY(0); }
        .free-shirt-modal__header { font-size: 22px; font-weight: 600; margin-bottom: 8px; text-transform: capitalize; }
        .free-shirt-modal__sub { color: #6f6f6f; font-size: 14px; margin-bottom: 16px; }
        .free-shirt-modal__body { display: grid; gap: 20px; }
        .free-shirt-modal__grid { display: grid; gap: 18px; align-items: start; }
        .free-shirt-modal__media {
          border-radius: 10px;
          overflow: hidden;
          background: #f6f6f6;
          aspect-ratio: 1 / 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .free-shirt-modal__media img { width: 100%; height: 100%; object-fit: cover; }
        .free-shirt-modal__field { display: grid; gap: 6px; }
        .free-shirt-modal__field label { font-size: 12px; text-transform: uppercase; letter-spacing: .06em; color: #6f6f6f; }
        .free-shirt-modal__select { width: 100%; padding: 10px 12px; border: 1px solid #d5d5d5; border-radius: 6px; background: #fff; }
        .free-shirt-modal__actions { display: grid; gap: 10px; }
        .free-shirt-modal__actions .btn { width: 100%; }
        .free-shirt-modal__close {
          position: absolute; top: 12px; right: 12px;
          width: 36px; height: 36px; border-radius: 50%;
          border: 1px solid #e5e5e5; background: #fff; font-size: 20px; cursor: pointer;
        }
        .free-shirt-modal__note { font-size: 13px; color: #666; }
        .free-shirt-modal__error { color: #b11; }
        body.free-shirt-modal-open { overflow: hidden; }
        @media (min-width: 740px){
          .free-shirt-modal__grid { grid-template-columns: 1fr 1.1fr; }
        }
        @media (max-width: 520px){
          .free-shirt-modal__content { padding: 18px; }
          .free-shirt-modal__header { font-size: 20px; }
        }
      `;
      document.head.appendChild(style);
    }

    function applyGiftModalCopy(modal, cfg){
      const titleEl = modal.querySelector('.free-shirt-modal__header');
      const subEl = modal.querySelector('.free-shirt-modal__sub');
      const addBtn = modal.querySelector('.free-shirt-modal__add');
      const addingLbl = cfg.addingLabel || 'Adding...';
      if (titleEl) titleEl.textContent = cfg.title || 'Claim your free gift';
      if (subEl) subEl.textContent = cfg.subtitle || '';
      const btnLabel = cfg.buttonLabel || 'Add free gift';
      if (addBtn && addBtn.textContent !== addingLbl) addBtn.textContent = btnLabel;
      const limitNote = modal.querySelector('.free-shirt-modal__limit-note');
      if (limitNote) limitNote.textContent = cfg.limitNote || 'Limit 1 per order.';
      const closeBtn = modal.querySelector('.free-shirt-modal__close');
      if (closeBtn) closeBtn.setAttribute('aria-label', cfg.closeLabel || 'Close');
      modal.dataset.giftAddingLabel = addingLbl;
      modal.dataset.giftUnavailableMessage = cfg.unavailableText || 'Selected variant is unavailable.';
    }

    function createGiftModal(product, copyCfg){
      ensureGiftModalStyles();
      const modal = document.createElement('div');
      modal.className = 'free-shirt-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-hidden', 'true');

      const featuredImage = product?.images?.[0] || '';
      const defaultVariant = (product.variants || []).find(v => v.available) || (product.variants || [])[0];
      const optionMarkup = (product.options || []).map((opt, idx) => {
        const values = opt.values || [];
        const optionsHtml = values.map(v => `<option value="${String(v).replace(/"/g, '&quot;')}">${String(v).replace(/</g, '&lt;')}</option>`).join('');
        return `
          <div class="free-shirt-modal__field">
            <label>${String(opt.name || '').replace(/</g, '&lt;')}</label>
            <select class="free-shirt-modal__select" data-option-index="${idx}">
              ${optionsHtml}
            </select>
          </div>
        `;
      }).join('');

      modal.innerHTML = `
        <div class="free-shirt-modal__backdrop" data-free-shirt-close></div>
        <div class="free-shirt-modal__content">
          <button class="free-shirt-modal__close" type="button" aria-label="" data-free-shirt-close>&times;</button>
          <div class="free-shirt-modal__header"></div>
          <div class="free-shirt-modal__sub"></div>
          <div class="free-shirt-modal__body">
            <div class="free-shirt-modal__grid">
              <div class="free-shirt-modal__media">${featuredImage ? `<img data-free-shirt-image src="${featuredImage}" alt="">` : ''}</div>
              <div class="free-shirt-modal__options">
                ${optionMarkup}
                <div class="free-shirt-modal__actions">
                  <button class="btn btn--primary btn--solid free-shirt-modal__add" type="button"></button>
                  <span class="free-shirt-modal__note free-shirt-modal__limit-note"></span>
                  <div class="free-shirt-modal__note free-shirt-modal__error" style="display:none;"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      const imgHolder = modal.querySelector('[data-free-shirt-image]');
      if (imgHolder) imgHolder.alt = product.title || '';

      applyGiftModalCopy(modal, copyCfg);

      if (defaultVariant) {
        modal.querySelectorAll('[data-option-index]').forEach((sel, idx) => {
          sel.value = defaultVariant.options[idx];
        });
      }

      modal.addEventListener('click', (e) => {
        if (e.target && e.target.matches('[data-free-shirt-close]')) closeGiftModal();
      });
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeGiftModal();
      });
      document.body.appendChild(modal);
      return modal;
    }

    function getSelectedVariant(product, modal){
      const selects = modal.querySelectorAll('[data-option-index]');
      const selectedOptions = Array.from(selects).map(sel => sel.value);
      return product.variants.find(v => selectedOptions.every((val, idx) => v.options[idx] === val)) || null;
    }

    function updateGiftModalState(product, modal){
      const addBtn = modal.querySelector('.free-shirt-modal__add');
      const errorEl = modal.querySelector('.free-shirt-modal__error');
      const variant = getSelectedVariant(product, modal);
      const imgEl = modal.querySelector('[data-free-shirt-image]');
      if (imgEl && variant?.featured_image?.src) {
        imgEl.src = variant.featured_image.src;
        imgEl.alt = `${product.title} - ${variant.title}`;
      }
      if (!variant || !variant.available) {
        addBtn.disabled = true;
        if (errorEl) {
          errorEl.style.display = 'block';
          errorEl.textContent = modal.dataset.giftUnavailableMessage || 'Selected variant is unavailable.';
        }
        return null;
      }
      addBtn.disabled = false;
      if (errorEl) errorEl.style.display = 'none';
      return variant;
    }

    function hideGiftModal(){
      if (!giftModalEl) return;
      giftModalEl.classList.remove('is-open');
      giftModalEl.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('free-shirt-modal-open');
      giftModalOpen = false;
    }

    function closeGiftModal(){
      const kind = giftModalEl?.dataset?.promoKind === 'modal' ? 'modal' : 'auto';
      hideGiftModal();
      dismissedModal[kind] = true;
    }

    function openGiftModal(product, copyCfg, promoKind){
      const pk = promoKind === 'modal' ? 'modal' : 'auto';
      if (giftModalOpen) return;
      if (pk === 'auto' && dismissedModal.auto) return;
      if (pk === 'modal' && dismissedModal.modal) return;
      const handle = copyCfg.handle || '';
      if (giftModalEl && giftModalProductHandle !== handle) {
        giftModalEl.remove();
        giftModalEl = null;
      }
      if (!giftModalEl) {
        giftModalEl = createGiftModal(product, copyCfg);
        giftModalProductHandle = handle;
      } else {
        applyGiftModalCopy(giftModalEl, copyCfg);
      }

      giftModalEl.dataset.promoKind = pk;

      const modal = giftModalEl;
      modal.querySelectorAll('[data-option-index]').forEach(sel => {
        if (!sel.dataset.giftModalBound) {
          sel.dataset.giftModalBound = 'true';
          sel.addEventListener('change', () => updateGiftModalState(product, modal), { passive: true });
        }
      });

      const addBtn = modal.querySelector('.free-shirt-modal__add');
      const btnLabel = copyCfg.buttonLabel || 'Add free gift';
      const addingLabel = copyCfg.addingLabel || modal.dataset.giftAddingLabel || 'Adding...';
      const propKey = pk === 'modal' ? '_fft_cart_gift_modal' : '_fft_cart_gift_auto';
      addBtn.onclick = async () => {
        const variant = updateGiftModalState(product, modal);
        if (!variant || addBtn.disabled) return;
        addBtn.disabled = true;
        addBtn.textContent = addingLabel;
        try {
          const fd = new FormData();
          fd.append('id', String(variant.id));
          fd.append('quantity','1');
          fd.append(`properties[${propKey}]`,'true');
          const res = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: fd
          });
          if (!res.ok) throw new Error('add failed');
          lastCartFetch = { ts: 0, data: null };
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:product:added', {
            detail: { variantId: variant.id, quantity: 1 },
            bubbles: true
          }));
          dismissedModal[pk] = false;
          hideGiftModal();
        } catch (e){
          console.error(e);
          addBtn.disabled = false;
          addBtn.textContent = btnLabel;
        }
      };

      updateGiftModalState(product, modal);
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('free-shirt-modal-open');
      giftModalOpen = true;
    }

    async function addGiftVariant(variantId, promoKind){
      if (isManagingGift) return;
      const pk = promoKind === 'modal' ? 'modal' : 'auto';
      const propKey = pk === 'modal' ? '_fft_cart_gift_modal' : '_fft_cart_gift_auto';
      isManagingGift = true;
      try {
        const fd = new FormData();
        fd.append('id', String(variantId));
        fd.append('quantity','1');
        fd.append(`properties[${propKey}]`,'true');
        const res = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: fd
        });
        if (res.ok){
          lastCartFetch = { ts: 0, data: null };
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setTimeout(() => { isManagingGift = false; }, 50);
      }
    }

    function readUiConfig(container){
      const emptySet = new Set();
      const emptyModal = {
        autoEnabled: false,
        autoHandle: '',
        autoThreshold: 0,
        modalEnabled: false,
        modalHandle: '',
        modalThreshold: 0,
        giftsActive: false,
        shippingLimit: 0,
        themeVariantSet: emptySet,
        modalTitle: 'Claim your free gift',
        modalSubtitle: '',
        modalButton: 'Add free gift',
        modalLimitNote: 'Limit 1 per order.',
        modalCloseLabel: 'Close',
        modalVariantUnavailable: 'Selected variant is unavailable.',
        modalAddingLabel: 'Adding...'
      };
      if (!container) return emptyModal;
      return {
        autoEnabled: container.getAttribute('data-cart-gift-auto-enabled') === 'true',
        autoHandle: (container.getAttribute('data-cart-gift-auto-handle') || '').trim(),
        autoThreshold: parseFloat(container.getAttribute('data-cart-gift-auto-threshold')) || 0,
        modalEnabled: container.getAttribute('data-cart-gift-modal-enabled') === 'true',
        modalHandle: (container.getAttribute('data-cart-gift-modal-handle') || '').trim(),
        modalThreshold: parseFloat(container.getAttribute('data-cart-gift-modal-threshold')) || 0,
        giftsActive: container.getAttribute('data-cart-gifts-active') === 'true',
        shippingLimit: parseFloat(container.getAttribute('data-free-shipping-limit')) || 0,
        themeVariantSet: parseThemeGiftVariantSet(container.getAttribute('data-fft-theme-gift-variant-ids')),
        modalTitle: container.getAttribute('data-free-gift-modal-title') || 'Claim your free gift',
        modalSubtitle: container.getAttribute('data-free-gift-modal-subtitle') || '',
        modalButton: container.getAttribute('data-free-gift-modal-button') || 'Add free gift',
        modalLimitNote: container.getAttribute('data-free-gift-modal-limit-note') || 'Limit 1 per order.',
        modalCloseLabel: container.getAttribute('data-free-gift-modal-close-label') || 'Close',
        modalVariantUnavailable: container.getAttribute('data-free-gift-modal-variant-unavailable') || 'Selected variant is unavailable.',
        modalAddingLabel: container.getAttribute('data-free-gift-modal-adding-label') || 'Adding...'
      };
    }

    function readCartSubtotalDollarsFromDom(){
      const el = document.querySelector('cart-items [data-cart-total], cart-drawer [data-cart-total], [data-cart-total]');
      if (!el || !el.hasAttribute('data-cart-total')) return null;
      const raw = parseInt(el.getAttribute('data-cart-total'), 10);
      if (Number.isNaN(raw)) return null;
      return raw / 100;
    }

    function computeTotalBasis(cart, themeVariantSet){
      if (!cart) return 0;
      let cents = cart.total_price || 0;
      for (const item of cart.items || []) {
        if (!isPromoGiftLine(item, themeVariantSet)) continue;
        const lp = item.final_line_price || 0;
        if (lp > 0) cents -= lp;
      }
      return cents / 100;
    }

    function resolveSpendBasisDollars(cart, themeVariantSet){
      const fromDom = readCartSubtotalDollarsFromDom();
      if (fromDom !== null) return fromDom;
      return computeTotalBasis(cart, themeVariantSet);
    }

    async function manageAutoGift(cart, totalBasis, cfg, themeVariantSet){
      if (!cfg.autoEnabled || !cfg.autoHandle || cfg.autoThreshold <= 0) {
        dismissedModal.auto = false;
        if (giftModalOpen && giftModalEl?.dataset?.promoKind === 'auto') hideGiftModal();
        const productWas = await ensureGiftProduct(cfg.autoHandle);
        if (productWas?.variants?.length) {
          const ids = giftVariantIdsFromProduct(productWas);
          if (cartHasPromoFor(cart, ids, 'auto', themeVariantSet)) await removePromoGiftVariantLines(ids, 'auto', themeVariantSet);
        }
        return;
      }

      const product = await ensureGiftProduct(cfg.autoHandle);
      if (!product?.variants?.length) return;
      const giftVariantIds = giftVariantIdsFromProduct(product);
      const hasPromo = cartHasPromoFor(cart, giftVariantIds, 'auto', themeVariantSet);

      if (totalBasis >= cfg.autoThreshold) {
        if (!hasPromo) {
          if (!giftNeedsVariantPicker(product)) {
            const v = product.variants.find(x => x.available) || product.variants[0];
            if (v?.available) await addGiftVariant(v.id, 'auto');
          } else {
            openGiftModal(product, {
              handle: cfg.autoHandle,
              title: cfg.modalTitle,
              subtitle: cfg.modalSubtitle,
              buttonLabel: cfg.modalButton,
              limitNote: cfg.modalLimitNote,
              closeLabel: cfg.modalCloseLabel,
              unavailableText: cfg.modalVariantUnavailable,
              addingLabel: cfg.modalAddingLabel
            }, 'auto');
          }
        }
        return;
      }

      dismissedModal.auto = false;
      if (giftModalOpen && giftModalEl?.dataset?.promoKind === 'auto') hideGiftModal();
      if (hasPromo) await removePromoGiftVariantLines(giftVariantIds, 'auto', themeVariantSet);
    }

    async function manageModalGift(cart, totalBasis, cfg, themeVariantSet){
      if (!cfg.modalEnabled || !cfg.modalHandle || cfg.modalThreshold <= 0) {
        dismissedModal.modal = false;
        if (giftModalOpen && giftModalEl?.dataset?.promoKind === 'modal') hideGiftModal();
        const productWas = await ensureGiftProduct(cfg.modalHandle);
        if (productWas?.variants?.length) {
          const ids = giftVariantIdsFromProduct(productWas);
          if (cartHasPromoFor(cart, ids, 'modal', themeVariantSet)) await removePromoGiftVariantLines(ids, 'modal', themeVariantSet);
        }
        return;
      }

      const product = await ensureGiftProduct(cfg.modalHandle);
      if (!product?.variants?.length) return;
      const giftVariantIds = giftVariantIdsFromProduct(product);
      const hasPromo = cartHasPromoFor(cart, giftVariantIds, 'modal', themeVariantSet);

      if (totalBasis >= cfg.modalThreshold) {
        if (!hasPromo) {
          if (!giftNeedsVariantPicker(product)) {
            const v = product.variants.find(x => x.available) || product.variants[0];
            if (v?.available) await addGiftVariant(v.id, 'modal');
          } else {
            openGiftModal(product, {
              handle: cfg.modalHandle,
              title: cfg.modalTitle,
              subtitle: cfg.modalSubtitle,
              buttonLabel: cfg.modalButton,
              limitNote: cfg.modalLimitNote,
              closeLabel: cfg.modalCloseLabel,
              unavailableText: cfg.modalVariantUnavailable,
              addingLabel: cfg.modalAddingLabel
            }, 'modal');
          }
        }
        return;
      }

      dismissedModal.modal = false;
      if (giftModalOpen && giftModalEl?.dataset?.promoKind === 'modal') hideGiftModal();
      if (hasPromo) await removePromoGiftVariantLines(giftVariantIds, 'modal', themeVariantSet);
    }

    async function manageGiftPromotions(){
      if (isManagingGift || managingGiftPromotions) return;
      const containers = getContainers();
      if (!containers.length) return;
      const container = containers[0];
      const cfg = readUiConfig(container);
      if (!cfg.giftsActive) return;

      managingGiftPromotions = true;
      try {
        const themeVariantSet = await resolveThemeGiftVariantSet(container, cfg);
        const cart = await getCartState(true);
        if (!cart) return;
        const totalBasis = resolveSpendBasisDollars(cart, themeVariantSet);

        await manageModalGift(cart, totalBasis, cfg, themeVariantSet);
        const cart2 = await getCartState(true);
        const basis2 = resolveSpendBasisDollars(cart2, themeVariantSet);
        await manageAutoGift(cart2, basis2, cfg, themeVariantSet);
      } finally {
        managingGiftPromotions = false;
      }
    }

    function scheduleDisabledPromoCleanup(){
      const c = getContainers()[0];
      if (!c || c.getAttribute('data-cart-gifts-active') === 'true') return;
      clearTimeout(disabledCleanupTimer);
      disabledCleanupTimer = setTimeout(() => removePromotionalGiftLines(), 600);
    }

    function updateMilestonesFast(){
      const containers = getContainers();
      if (!containers || !containers.length) return;
      const container = containers[0];
      const cfg = readUiConfig(container);
      const shippingLimit = cfg.shippingLimit;
      const autoT = cfg.autoThreshold;
      const modalT = cfg.modalThreshold;
      const autoOn = cfg.autoEnabled && !!cfg.autoHandle && autoT > 0;
      const modalOn = cfg.modalEnabled && !!cfg.modalHandle && modalT > 0;

      let cartTotal = 0;
      const totalEl = document.querySelector('[data-cart-total]');
      if (totalEl){
        const v = parseFloat(totalEl.getAttribute('data-cart-total')) || 0;
        if (v > 0) cartTotal = v / 100;
      }

      if (cartTotal === 0){
        const items = document.querySelectorAll('[data-cart-item]');
        if (items && items.length){
          let calc = 0;
          items.forEach(it => {
            const lp = it.querySelector('[data-line-price]');
            const q = it.querySelector('input[type="number"]')?.value || it.querySelector('[data-qty]')?.textContent || '1';
            const qty = parseFloat(q) || 1;
            let price = 0;
            if (lp) price = parseFloat(lp.getAttribute('data-line-price')) || parseFloat((lp.textContent||'').replace(/[^0-9.]/g,'')) || 0;
            else price = parseFloat(it.querySelector('[data-item-price]')?.getAttribute('data-item-price') || it.querySelector('[data-price]')?.getAttribute('data-price') || (it.querySelector('.cart__price')?.textContent||'').replace(/[^0-9.]/g,'')) || 0;
            if (price > 1000) price = price/100;
            calc += (price * (qty || 1));
          });
          if (calc > 0) cartTotal = calc;
        }
      }

      const shippingMilestone = container.querySelector('.free-shipping__milestone--shipping');
      const autoMilestone = container.querySelector('.free-shipping__milestone--auto-gift');
      const modalMilestone = container.querySelector('.free-shipping__milestone--modal-gift');
      const showShipping = shippingLimit > 0 && cartTotal >= shippingLimit;
      const showAuto = autoOn && autoMilestone && cartTotal >= autoT;
      const showModal = modalOn && modalMilestone && cartTotal >= modalT;

      if (shippingMilestone){
        shippingMilestone.classList.toggle('is-reached', showShipping);
      }
      if (autoMilestone){
        autoMilestone.classList.toggle('is-reached', showAuto);
      }
      if (modalMilestone){
        modalMilestone.classList.toggle('is-reached', showModal);
      }

      container.classList.toggle('has-free-shipping', showShipping);
      container.classList.toggle('has-auto-gift', showAuto);
      container.classList.toggle('has-modal-gift', showModal);

      const progressBar = container.querySelector('[data-progress-bar]');
      if (progressBar){
        let max = Math.max(shippingLimit, 1);
        if (autoOn) max = Math.max(max, autoT);
        if (modalOn) max = Math.max(max, modalT);
        const pct = max > 0 ? Math.min((cartTotal / max) * 100, 100) : 0;
        progressBar.setAttribute('value', pct.toFixed(2));
      }

      if (!cfg.giftsActive) scheduleDisabledPromoCleanup();
      else manageGiftPromotions();
    }

    let debounceTimer = 0;
    function scheduleUpdate(ms = 30){
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        requestAnimationFrame(updateMilestonesFast);
      }, ms);
    }

    const observer = new MutationObserver((mutations) => {
      let should = false;
      for (const m of mutations){
        if (m.type === 'attributes' && (m.attributeName === 'data-cart-total' || m.attributeName === 'class')){
          should = true; break;
        }
        if (m.addedNodes && m.addedNodes.length){
          should = true; break;
        }
      }
      if (should) scheduleUpdate(20);
    });

    observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['data-cart-total','class']});
    const cartDrawer = document.querySelector('#cart-drawer');
    if (cartDrawer) observer.observe(cartDrawer, {attributes: true, childList: true, subtree: true});

    ['theme:cart:update','theme:cart:change','theme:product:added','cart:updated'].forEach(ev => {
      document.addEventListener(ev, () => scheduleUpdate(15));
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart__quantity-minus, .cart__quantity-plus, .quantity__minus, .quantity__plus, button[name="decrease"], button[name="increase"]');
      if (btn) scheduleUpdate(20);
    }, {passive: true});

    document.addEventListener('input', (e) => {
      if (e.target && (e.target.matches('input[data-qty]') || e.target.matches('input[name*="quantity"]'))) scheduleUpdate(30);
    }, {passive: true});

    setInterval(()=> scheduleUpdate(100), 3000);

    requestAnimationFrame(() => {
      scheduleUpdate(0);
      setTimeout(() => {
        const c = getContainers()[0];
        if (c && c.getAttribute('data-cart-gifts-active') !== 'true') {
          removePromotionalGiftLines();
        }
      }, 500);
    });
  }
})();

// Size Swatches with Add to Cart functionality - Optimized
(function(){
  'use strict';

  let isProcessing = false;
  const processingSwatches = new Set();

  if (typeof theme === 'undefined') {
    window.addEventListener('DOMContentLoaded', () => initSizeSwatches());
  } else {
    initSizeSwatches();
  }

  function initSizeSwatches(){
    const sizeSwatches = document.querySelectorAll('.size-swatch:not([disabled])');
    
    sizeSwatches.forEach(swatch => {
      if (!swatch.hasAttribute('data-size-listener')) {
        swatch.setAttribute('data-size-listener', 'true');
        swatch.addEventListener('click', handleSizeSwatchClick, { passive: true });
      }
    });
  }

  function handleSizeSwatchClick(e){
    e.preventDefault();
    e.stopPropagation();
    
    const swatch = e.currentTarget;
    const variantId = swatch.getAttribute('data-variant-id');
    const productHandle = swatch.getAttribute('data-product-handle');
    
    if (!variantId || swatch.disabled || swatch.classList.contains('is-loading') || processingSwatches.has(swatch)) {
      return;
    }

    // Use requestAnimationFrame for instant visual feedback
    requestAnimationFrame(() => {
      addToCartFromSizeSwatch(swatch, variantId, productHandle);
    });
  }

  async function addToCartFromSizeSwatch(swatch, variantId, productHandle){
    // Prevent duplicate clicks
    if (processingSwatches.has(swatch)) return;
    processingSwatches.add(swatch);

    // Immediate visual feedback
    swatch.classList.add('is-loading');
    swatch.disabled = true;

    const productContainer = swatch.closest('.product-item__size-swatches');
    const allSwatches = productContainer ? productContainer.querySelectorAll('.size-swatch') : [];
    
    // Disable other swatches temporarily
    allSwatches.forEach(s => {
      if (s !== swatch) {
        s.classList.add('is-disabled');
        s.disabled = true;
      }
    });

    try {
      // Use the theme's cart system if available for better integration
      const cartItems = document.querySelector('cart-items');
      
      if (cartItems && typeof cartItems.addToCart === 'function') {
        // Use theme's addToCart method for better integration
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        
        // Create a temporary button for the theme's addToCart method
        const tempButton = document.createElement('button');
        tempButton.setAttribute('data-add-to-cart', '');
        tempButton.style.display = 'none';
        
        cartItems.addToCart(formData, tempButton);
        
        // Wait a bit for the cart to update
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        // Fallback to direct fetch - optimized
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
        
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: formData,
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.status) {
          throw new Error(data.description || 'Failed to add to cart');
        }

        // Dispatch cart:add event to trigger theme's cart system
        document.dispatchEvent(new CustomEvent('theme:cart:add', {
          detail: { variantId, quantity: 1 },
          bubbles: true
        }));
      }

      // Success - immediate visual feedback
      swatch.classList.remove('is-loading');
      swatch.classList.add('is-added');
      
      // Dispatch cart refresh immediately
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      
      // Dispatch product added event (this triggers cart drawer opening)
      document.dispatchEvent(new CustomEvent('theme:product:added', {
        detail: { 
          variantId, 
          productHandle,
          response: { items: [{ variant_id: parseInt(variantId), quantity: 1 }] }
        },
        bubbles: true
      }));
      
      // Also explicitly trigger cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.openCartDrawer === 'function') {
        setTimeout(() => {
          cartDrawer.openCartDrawer(true);
        }, 50);
      }

      // Reset UI after brief delay
      setTimeout(() => {
        swatch.classList.remove('is-added');
        resetSwatches(allSwatches);
        processingSwatches.delete(swatch);
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      swatch.classList.remove('is-loading');
      swatch.classList.add('is-error');
      
      // Show error briefly, then reset
      setTimeout(() => {
        swatch.classList.remove('is-error');
        resetSwatches(allSwatches);
        processingSwatches.delete(swatch);
      }, 2000);
    }
  }

  function resetSwatches(swatches) {
    swatches.forEach(s => {
      s.classList.remove('is-disabled', 'is-loading', 'is-added', 'is-error');
      const isAvailable = s.getAttribute('data-variant-id') && !s.classList.contains('size-swatch--soldout');
      s.disabled = !isAvailable;
    });
  }

  // Re-initialize when new products are loaded (optimized observer)
  const observer = new MutationObserver((mutations) => {
    let hasNewSwatches = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          const newSwatches = node.querySelectorAll ? node.querySelectorAll('.size-swatch:not([disabled]):not([data-size-listener])') : [];
          if (newSwatches.length > 0) {
            hasNewSwatches = true;
            newSwatches.forEach(swatch => {
              swatch.setAttribute('data-size-listener', 'true');
              swatch.addEventListener('click', handleSizeSwatchClick, { passive: true });
            });
          }
        }
      });
    });
  });

  // Observe only product items to reduce overhead
  const productItems = document.querySelectorAll('.product-item');
  productItems.forEach(item => {
    observer.observe(item, { childList: true, subtree: true });
  });
  
  // Also observe body for dynamically added product grids
  observer.observe(document.body, { childList: true, subtree: false });
})();
